import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    ArrowDownTrayIcon,
    ArrowsRightLeftIcon,
    ChartPieIcon,
    CheckIcon,
    ChevronDownIcon,
    DocumentTextIcon,
    ExclamationTriangleIcon,
    FunnelIcon,
    MagnifyingGlassIcon,
    PlusIcon,
    SparklesIcon,
    TagIcon,
    TrashIcon,
    XMarkIcon,
} from "@heroicons/react/24/outline";
import { LoaderSpinner } from "../../components/LoaderSpinner";
import { ProductCatalogSelectFilter } from "../../components/productCatalog/ProductCatalogSelectFilter";
import {
    deleteInventorySkuMapping,
    fetchAmazonSkuMappings,
    fetchCatalogSkus,
    selectAmazonSkuMappingItems,
    selectAmazonSkuMappingLoading,
    fetchInventorySkuMappingStats,
    fetchInventorySkuMappings,
    selectInventorySkuMappingItems,
    selectInventorySkuMappingListLoading,
    selectInventorySkuMappingStats,
    updateInventorySkuMapping,
} from "../../slices/inventorySkuMappingSlice";
import { toast } from "react-toastify";
import AddRowModal from "./AddRowModal";
import AddProductDrawer from "./AddProductDrawer";
import AmazonMappingTable from "./AmazonMappingTable";
import FinalMappingTable from "./FinalMappingTable";
import SkuMappingManualTable from "./SkuMappingManualTable";

const SKU_MAPPING_TABS = [
    { id: "manual", label: "Manual SKU Mapping" },
    { id: "shopify", label: "Shopify Auto Mapping", count: 0 },
    { id: "amazon", label: "Amazon SKU Mapping", count: 0 },
    { id: "final", label: "SKU Mapping Final", count: 0 },
];

const CHANNEL_FILTER_OPTIONS = [
    { value: "shopify", label: "Shopify" },
    { value: "amazon", label: "Amazon" },
];
const CLEAR_SUPPLY_OPTION = { id: "__clear_supply__", sku: "" };
const MARKETPLACE_SUFFIX_RE = /-(FBA|FBM)$/i;

function normalizeSupplyLookupQuery(value) {
    const normalized = String(value || "").trim();
    if (!normalized) return "";
    return normalized.replace(MARKETPLACE_SUFFIX_RE, "");
}

function formatRowsLabel(count) {
    return `${count} ${count === 1 ? "ROW" : "ROWS"}`;
}

function escapeCsvField(value) {
    const normalized = value == null ? "" : String(value);
    if (/[",\r\n]/.test(normalized)) {
        return `"${normalized.replace(/"/g, '""')}"`;
    }
    return normalized;
}

function normalizeComparableSku(value) {
    return String(value || "")
        .trim()
        .toUpperCase()
        .replace(/\s+/g, "");
}

function BulkPopoverCard({ className = "", children }) {
    return (
        <div
            className={`absolute left-2 top-full z-50 mt-1.5 w-[min(32rem,calc(100vw-2rem))] rounded-xl border border-gray-200 bg-white p-4 shadow-[0_8px_32px_rgba(0,0,0,0.12),0_2px_8px_rgba(0,0,0,0.06)] animate-in fade-in-0 slide-in-from-top-1 duration-150 ${className}`}
        >
            {children}
        </div>
    );
}

function BulkActionButton({
    Icon,
    label,
    onClick,
    isOpen = false,
    disabled = false,
    danger = false,
}) {
    const buttonClass = danger
        ? isOpen
            ? "bg-[#991b1b] text-[#ffb4ad] shadow-sm"
            : "bg-[#7f1d1d] text-[#ffb4ad] hover:bg-[#991b1b] hover:text-[#ffb4ad]"
        : isOpen
          ? "bg-[#1f2937] text-white"
          : "bg-[#111827] text-white/90 hover:bg-[#1f2937] hover:text-white";
    const iconClass = danger ? "text-[#ffb4ad]" : "text-current";
    const chevronClass = danger ? "text-[#ffb4ad]" : "opacity-70";

    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            className={[
                "group inline-flex h-8 items-center gap-1.5 rounded-md px-3 text-xs font-semibold transition-colors",
                "focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60 disabled:cursor-not-allowed disabled:opacity-50",
                buttonClass,
            ].join(" ")}
        >
            <Icon className={`h-4 w-4 shrink-0 ${iconClass}`} aria-hidden />
            <span>{label}</span>
            <ChevronDownIcon className={`h-3.5 w-3.5 shrink-0 ${chevronClass}`} aria-hidden />
        </button>
    );
}

function BulkChannelEditor({ rows, busy, onApply }) {
    const [chosenChannel, setChosenChannel] = useState("");

    return (
        <BulkPopoverCard className="!w-[242px]">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Change Channel · {formatRowsLabel(rows.length)}
            </p>
            <div className="mt-3 flex flex-col gap-0.5">
                {[
                    { value: "SHOPIFY", label: "Shopify" },
                    { value: "AMAZON", label: "Amazon" },
                ].map((option) => (
                    <button
                        key={option.value}
                        type="button"
                        disabled={busy}
                        className={`flex w-full items-center justify-between rounded px-2.5 py-2 text-left text-sm text-gray-900 transition-colors disabled:opacity-50 ${
                            chosenChannel === option.value ? "bg-gray-100" : "hover:bg-gray-50"
                        }`}
                        onClick={() => setChosenChannel(option.value)}
                    >
                        <span>{option.label}</span>
                        {chosenChannel === option.value ? (
                            <CheckIcon className="h-3.5 w-3.5 text-gray-700" aria-hidden />
                        ) : null}
                    </button>
                ))}
            </div>
            {chosenChannel ? (
                <button
                    type="button"
                    disabled={busy}
                    className="mt-3 inline-flex h-9 w-full items-center justify-center rounded-md bg-[#111827] px-3 text-sm font-semibold text-white transition-colors hover:bg-black disabled:opacity-50"
                    onClick={() => onApply(chosenChannel)}
                >
                    Apply
                </button>
            ) : null}
        </BulkPopoverCard>
    );
}

function BulkNotesEditor({ rows, busy, onApply }) {
    const sharedNote = useMemo(() => {
        if (rows.length === 0) return "";
        const first = rows[0]?.notes || "";
        return rows.every((row) => (row.notes || "") === first) ? first : "";
    }, [rows]);
    const [value, setValue] = useState(sharedNote);

    useEffect(() => {
        setValue(sharedNote);
    }, [sharedNote]);

    return (
        <BulkPopoverCard className="!w-[322px]">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Edit Notes · {formatRowsLabel(rows.length)}
            </p>
            {sharedNote ? <p className="mt-3 text-xs text-gray-400">Editing existing note</p> : null}
            <textarea
                value={value}
                onChange={(e) => setValue(e.target.value)}
                rows={4}
                className="mt-2 w-full resize-none rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-900 outline-none focus:ring-2 focus:ring-primary"
                placeholder="Enter notes..."
            />
            <button
                type="button"
                disabled={busy}
                className="mt-4 inline-flex h-9 w-full items-center justify-center rounded-md bg-[#111827] px-3 text-sm font-semibold text-white transition-colors hover:bg-[#1f2937] disabled:opacity-50"
                onClick={() => onApply(value)}
            >
                Apply
            </button>
        </BulkPopoverCard>
    );
}

function BulkDeleteConfirm({ rows, busy, onCancel, onDelete }) {
    return (
        <BulkPopoverCard className="!w-[290px]">
            <div className="flex items-center gap-2 mb-3">
                <ExclamationTriangleIcon className="h-4 w-4 shrink-0 text-red-500" aria-hidden />
                <p className="text-sm font-semibold text-gray-900">
                        Delete {rows.length} {rows.length === 1 ? "row" : "rows"}?
                </p>
            </div>
            <p className="mb-4 text-xs text-gray-500">
                This will permanently remove the selected mapping{rows.length === 1 ? "" : "s"} from Manual SKU Mapping.
            </p>
            <div className="flex items-center gap-2">
                <button
                    type="button"
                    disabled={busy}
                    className="inline-flex h-10 flex-1 items-center justify-center rounded-md bg-gray-100 px-3 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-200 disabled:opacity-50"
                    onClick={onCancel}
                >
                    Cancel
                </button>
                <button
                    type="button"
                    disabled={busy}
                    className="inline-flex h-10 flex-1 items-center justify-center rounded-md bg-red-500 px-3 text-sm font-semibold text-white transition-colors hover:bg-red-700 disabled:opacity-50"
                    onClick={onDelete}
                >
                    Delete
                </button>
            </div>
        </BulkPopoverCard>
    );
}

function BulkSupplyEditor({ rows, busy, loadCatalogSkus, onApply, onClearCurrent }) {
    const singleRow = rows.length === 1 ? rows[0] : null;
    const sharedSupply =
        rows.length > 0 &&
        rows.every((row) => row.supply_product_id === rows[0]?.supply_product_id) &&
        rows[0]?.supply_product_id
            ? { id: rows[0].supply_product_id, sku: rows[0].supply_sku || "" }
            : null;
    const [query, setQuery] = useState("");
    const [debouncedQuery, setDebouncedQuery] = useState("");
    const [results, setResults] = useState([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const [suggestion, setSuggestion] = useState(null);
    const [suggestionLoading, setSuggestionLoading] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);

    useEffect(() => {
        setQuery("");
        setDebouncedQuery("");
        setResults([]);
        const nextSuggestion =
            singleRow?.suggested_supply_product_id
                ? {
                      id: singleRow.suggested_supply_product_id,
                      sku: singleRow.suggested_supply_sku || "",
                      status: singleRow.suggested_supply_status || null,
                  }
                : null;
        setSuggestion(nextSuggestion);
        setSelectedProduct(null);
    }, [
        rows,
        singleRow?.suggested_supply_product_id,
        singleRow?.suggested_supply_sku,
        singleRow?.suggested_supply_status,
    ]);

    useEffect(() => {
        setSuggestionLoading(false);
    }, [singleRow]);

    useEffect(() => {
        const t = setTimeout(() => setDebouncedQuery(normalizeSupplyLookupQuery(query)), 250);
        return () => clearTimeout(t);
    }, [query]);

    useEffect(() => {
        if (!debouncedQuery) {
            setResults([]);
            setSearchLoading(false);
            return;
        }

        let cancelled = false;
        setSearchLoading(true);
        loadCatalogSkus({
            picker: "supply",
            includeNameMatch: true,
            limit: 50,
            ...(singleRow?.execution_product_id ? { excludeProductId: singleRow.execution_product_id } : {}),
            search: debouncedQuery,
        })
            .then((items) => {
                if (!cancelled) setResults(items || []);
            })
            .catch(() => {
                if (!cancelled) setResults([]);
            })
            .finally(() => {
                if (!cancelled) setSearchLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [debouncedQuery, loadCatalogSkus, singleRow]);

    const showResults = debouncedQuery.length > 0;
    const shouldShowCurrent = Boolean(sharedSupply?.sku) && !selectedProduct;
    const showSuggestionCard = Boolean(singleRow && suggestion) && !selectedProduct;
    const shouldShowNewValue = Boolean(selectedProduct);
    const applyLabel = `Apply to ${rows.length} ${rows.length === 1 ? "row" : "rows"}`;
    const visibleExecutionRows = rows.slice(0, 5);
    const hiddenExecutionCount = Math.max(rows.length - visibleExecutionRows.length, 0);

    return (
        <BulkPopoverCard className="!w-[354px]">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Change Supply SKU · {formatRowsLabel(rows.length)}
            </p>

            <div className="mb-3 border-b border-gray-100 pb-2.5">
                <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                    {rows.length === 1 ? "Execution SKU" : "Execution SKUs"}
                </p>
                <div className="flex flex-wrap gap-1">
                    {visibleExecutionRows.map((row) => (
                        <span
                            key={row.id}
                            className="inline-flex items-center rounded border border-gray-200 bg-gray-50 px-[10px] py-0.5 text-xs font-medium text-gray-700"
                        >
                            {row.execution_sku}
                        </span>
                    ))}
                    {hiddenExecutionCount > 0 ? (
                        <span className="text-xs text-gray-400 self-center">
                            + {hiddenExecutionCount} more
                        </span>
                    ) : null}
                </div>
            </div>

            {shouldShowCurrent ? (
                <div className="mb-3">
                    <p className="mb-1.5 text-xs text-gray-400">Current</p>
                    <div className="flex flex-wrap items-center gap-1.5">
                        <span className="inline-flex items-center gap-1.5 rounded border border-gray-300 bg-white px-2.5 py-1 text-sm font-medium text-gray-800">
                            <span className="font-mono">{sharedSupply.sku}</span>
                            <button
                                type="button"
                                disabled={busy}
                                className="ml-0.5 text-gray-400 transition-colors hover:text-gray-700"
                                title="Clear Supply SKU (stay open to assign new)"
                                onClick={() => onClearCurrent?.()}
                            >
                                <XMarkIcon className="h-3 w-3" aria-hidden />
                            </button>
                        </span>
                    </div>
                </div>
            ) : null}

            {showSuggestionCard ? (
                <div className="mb-3 rounded-md border border-[#a8d5a2] bg-[#f6fff0] p-2.5">
                    <p className="mb-1.5 text-xs font-medium text-[#3a7d44]">Barcode suggestion</p>
                    <div>
                        {suggestionLoading ? (
                            <div className="flex items-center gap-2 rounded-md border border-[#a8d5a2] bg-white px-2.5 py-2 text-sm text-[#3a7d44]">
                                Looking for suggested supply SKU...
                            </div>
                        ) : suggestion ? (
                            <button
                                type="button"
                                className="flex w-full items-center justify-between rounded border border-[#a8d5a2] bg-white px-2.5 py-2 text-left transition-colors hover:bg-green-50/40"
                                onClick={() => {
                                    setSelectedProduct(suggestion);
                                    setQuery("");
                                    setDebouncedQuery("");
                                    setResults([]);
                                }}
                            >
                                <div>
                                    <p className="font-mono text-sm font-semibold text-gray-900">{suggestion.sku}</p>
                                </div>
                                <CheckIcon className="h-3.5 w-3.5 shrink-0 text-[#3a7d44]" aria-hidden />
                            </button>
                        ) : (
                            <div className="rounded-md border border-gray-200 bg-white px-2.5 py-2 text-sm text-gray-500">
                                No barcode suggestion available.
                            </div>
                        )}
                    </div>
                </div>
            ) : null}

            {shouldShowNewValue && selectedProduct.sku ? (
                <div className="mb-3">
                    <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-400">New value</p>
                    <div className="inline-flex items-center gap-2 rounded-md border border-blue-200 bg-blue-50 px-2.5 py-1.5 text-sm text-blue-900">
                        <span className="font-medium">{selectedProduct.sku}</span>
                        <button
                            type="button"
                            className="text-blue-500 transition-colors hover:text-blue-700"
                            onClick={() => setSelectedProduct(null)}
                            aria-label="Clear selected supply SKU"
                        >
                            <XMarkIcon className="h-4 w-4" aria-hidden />
                        </button>
                    </div>
                </div>
            ) : null}

            <div className="relative mb-1">
                <MagnifyingGlassIcon className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
                <input
                    type="search"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search SKU or product..."
                    className="w-full rounded no-search-input-chrome border border-gray-200 bg-white py-2 pl-8 pr-3 text-sm text-gray-900 outline-none transition-colors placeholder:text-gray-400 focus:border-gray-400"
                />
            </div>

            <div className="max-h-48 overflow-y-auto -mx-1 px-1">
                {searchLoading ? (
                    <div className="px-3 py-3 text-center text-sm text-gray-500">Searching supply SKUs...</div>
                ) : showResults ? (
                    results.length > 0 ? (
                        results.map((item) => {
                            const isSelected = selectedProduct?.id === item.id;
                            return (
                                <button
                                    key={item.id}
                                    type="button"
                                    className={`flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm font-normal hover:bg-gray-50 ${
                                        isSelected ? "bg-gray-50" : ""
                                    }`}
                                    onClick={() => {
                                        setSelectedProduct(item);
                                        setQuery("");
                                        setDebouncedQuery("");
                                        setResults([]);
                                    }}
                                >
                                    <span className="min-w-0 flex-1">
                                        <span className="block truncate font-semibold text-gray-900">{item.sku}</span>
                                    </span>
                                    {isSelected ? <CheckIcon className="h-4 w-4 text-gray-700" aria-hidden /> : null}
                                </button>
                            );
                        })
                    ) : (
                        <div className="px-3 py-3 text-center text-sm text-gray-500">No results</div>
                    )
                ) : (
                    <div className="py-3 text-center text-xs text-gray-400">Type to search supply SKUs</div>
                )}
            </div>
            {shouldShowNewValue ? (
                <button
                    type="button"
                    disabled={busy || !selectedProduct}
                    className="mt-4 inline-flex h-9 w-full items-center justify-center rounded-md bg-[#111827] px-3 text-sm font-semibold text-white transition-colors hover:bg-[#1f2937] disabled:opacity-50"
                    onClick={() => onApply(selectedProduct)}
                >
                    {applyLabel}
                </button>
            ) : null}
        </BulkPopoverCard>
    );
}

const getPreviewOfTheTable = (tabId) => {
    switch (tabId) {
        case "manual":
            return "Manually connect rogue / legacy execution SKUs to current supply SKUs";
        case "shopify":
            return "Auto-derived: Execution SKU = Supply SKU for all Shopify catalog products. Read-only.";
        case "amazon":
            return "Auto-derived from Catalog: Supply SKU + -FBA / -FBM suffix. Virtual SKUs — not ordered from suppliers. Read-only.";
        case "final":
            return "Canonical union of Manual + Amazon Auto. Manual mappings take precedence. Read-only.";
        default:
            return null;
    }

}

const SKUMapping = () => {
    const dispatch = useDispatch();
    const mappingStats = useSelector(selectInventorySkuMappingStats);
    const mappingItems = useSelector(selectInventorySkuMappingItems);
    const mappingsLoading = useSelector(selectInventorySkuMappingListLoading);
    const amazonMappingItems = useSelector(selectAmazonSkuMappingItems);
    const amazonMappingsLoading = useSelector(selectAmazonSkuMappingLoading);
    const bulkToolbarRef = useRef(null);

    const [activeTabId, setActiveTabId] = useState("manual");
    const [searchSku, setSearchSku] = useState("");
    const [selectedChannels, setSelectedChannels] = useState([]);
    const [addRowOpened, setAddRowOpened] = useState(false);
    const [addProductOpened, setAddProductOpened] = useState(false);
    const [manualTableRefreshKey, setManualTableRefreshKey] = useState(0);
    const [selectionResetKey, setSelectionResetKey] = useState(0);
    const [manualRowCount, setManualRowCount] = useState(0);
    const [unmappedOnly, setUnmappedOnly] = useState(false);
    const [selectedMappingIds, setSelectedMappingIds] = useState([]);
    const [openBulkAction, setOpenBulkAction] = useState(null);
    const [bulkBusy, setBulkBusy] = useState(false);
    const [exportingCsv, setExportingCsv] = useState(false);

    const loadMappingStats = useCallback(() => {
        dispatch(fetchInventorySkuMappingStats());
    }, [dispatch]);

    useEffect(() => {
        loadMappingStats();
    }, [manualTableRefreshKey, loadMappingStats]);

    useEffect(() => {
        dispatch(fetchAmazonSkuMappings()).then((action) => {
            if (fetchAmazonSkuMappings.rejected.match(action)) {
                toast.error("Could not load Amazon SKU mappings.", { position: "bottom-right" });
            }
        });
    }, [dispatch]);

    const mappedCount = mappingStats?.mapped ?? 0;
    const unmappedCount = mappingStats?.unmapped ?? 0;
    /** Total manual mapping rows from API stats (not filtered by search / slicers). */
    const manualTotalRowCount = mappingStats === null ? null : mappedCount + unmappedCount;
    const selectedIdsSet = useMemo(() => new Set(selectedMappingIds), [selectedMappingIds]);
    const selectedRows = useMemo(
        () => mappingItems.filter((item) => selectedIdsSet.has(item.id)),
        [mappingItems, selectedIdsSet]
    );
    const selectedCount = selectedRows.length;
    const suggestionsCount = useMemo(
        () => mappingItems.filter((item) => item.has_suggestion && !item.has_supply).length,
        [mappingItems]
    );
    const selectedSuggestionRows = useMemo(
        () => selectedRows.filter((item) => item.has_suggestion && item.suggested_supply_product_id && !item.has_supply),
        [selectedRows]
    );
    const selectedSuggestionsCount = selectedSuggestionRows.length;
    const filteredAmazonRows = useMemo(() => {
        const q = searchSku.trim().toLowerCase();
        if (!q) return amazonMappingItems;

        return amazonMappingItems.filter((row) => {
            const blob = [
                row.execution_sku,
                row.canonical_product_name,
                row.type,
                row.supply_sku,
                row.barcode,
                row.status,
                row.forecastable ? "yes" : "no",
            ]
                .filter(Boolean)
                .join(" ")
                .toLowerCase();
            return blob.includes(q);
        });
    }, [amazonMappingItems, searchSku]);
    const amazonTotalRowCount = amazonMappingItems.length;
    const finalRows = useMemo(() => {
        const manualRows = (mappingItems || []).map((row) => ({
            id: `manual-${row.id}`,
            execution_sku: row.execution_sku || "-",
            canonical_product_name: row.canonical_product_name || "-",
            channel: row.channel || "AMAZON",
            supply_sku: row.supply_sku || "",
            source: "MANUAL",
        }));

        const takenExecutionSkus = new Set(manualRows.map((row) => normalizeComparableSku(row.execution_sku)));
        const amazonRows = (amazonMappingItems || [])
            .filter((row) => !takenExecutionSkus.has(normalizeComparableSku(row.execution_sku)))
            .map((row) => ({
                id: `amazon-${row.id}`,
                execution_sku: row.execution_sku || "-",
                canonical_product_name: row.canonical_product_name || "-",
                channel: "AMAZON",
                supply_sku: row.supply_sku || "",
                source: row.type === "FBA" ? "AMAZON_FBA" : "AMAZON_FBM",
            }));

        const combined = [...manualRows, ...amazonRows];
        const counts = new Map();

        combined.forEach((row) => {
            const key = normalizeComparableSku(row.execution_sku);
            if (!key) return;
            counts.set(key, (counts.get(key) || 0) + 1);
        });

        return combined.map((row) => ({
            ...row,
            duplicate_flag: (counts.get(normalizeComparableSku(row.execution_sku)) || 0) > 1,
        }));
    }, [mappingItems, amazonMappingItems]);
    const filteredFinalRows = useMemo(() => {
        let list = finalRows;
        const q = searchSku.trim().toLowerCase();

        if (q) {
            list = list.filter((row) => {
                const blob = [
                    row.execution_sku,
                    row.canonical_product_name,
                    row.channel,
                    row.supply_sku,
                    row.source,
                ]
                    .filter(Boolean)
                    .join(" ")
                    .toLowerCase();
                return blob.includes(q);
            });
        }

        const channels = new Set((selectedChannels || []).map((channel) => String(channel).toLowerCase()));
        if (channels.size > 0 && channels.size < 2) {
            list = list.filter((row) => channels.has(String(row.channel || "").toLowerCase()));
        }

        return list;
    }, [finalRows, searchSku, selectedChannels]);
    const finalTotalRowCount = finalRows.length;
    const exportableManualRows = useMemo(() => {
        let list = mappingItems;
        const q = searchSku.trim().toLowerCase();

        if (q) {
            list = list.filter((row) => {
                const blob = [
                    row.execution_sku,
                    row.supply_sku,
                    row.canonical_product_name,
                    row.notes,
                    row.barcode,
                ]
                    .filter(Boolean)
                    .join(" ")
                    .toLowerCase();
                return blob.includes(q);
            });
        }

        const channels = new Set((selectedChannels || []).map((channel) => String(channel).toLowerCase()));
        if (channels.size > 0 && channels.size < 2) {
            list = list.filter((row) => {
                const key = row.channel === "AMAZON" ? "amazon" : "shopify";
                return channels.has(key);
            });
        }

        if (unmappedOnly) {
            list = list.filter((row) => !row.has_supply);
        }

        return list;
    }, [mappingItems, searchSku, selectedChannels, unmappedOnly]);

    const resetBulkSelection = useCallback(() => {
        setSelectedMappingIds([]);
        setSelectionResetKey((key) => key + 1);
        setOpenBulkAction(null);
    }, []);

    const refreshMappings = useCallback(async () => {
        await Promise.all([
            dispatch(fetchInventorySkuMappings()).unwrap(),
            dispatch(fetchInventorySkuMappingStats()).unwrap(),
        ]);
    }, [dispatch]);

    const loadCatalogSkus = useCallback(
        (params) => dispatch(fetchCatalogSkus(params)).unwrap(),
        [dispatch]
    );

    const runBulkAction = useCallback(
        async (runner, successMessage, fallbackMessage) => {
            if (selectedRows.length === 0) return;
            setBulkBusy(true);
            try {
                await runner();
                await refreshMappings();
                toast.success(successMessage, { position: "bottom-right" });
                resetBulkSelection();
            } catch (err) {
                const msg = typeof err === "string" ? err : err?.message || fallbackMessage;
                toast.error(msg, { position: "bottom-right" });
            } finally {
                setBulkBusy(false);
            }
        },
        [refreshMappings, resetBulkSelection, selectedRows.length]
    );

    const applyBulkChannel = useCallback(
        (channel) =>
            runBulkAction(
                () =>
                    Promise.all(
                        selectedRows.map((row) =>
                            dispatch(updateInventorySkuMapping({ id: row.id, body: { channel } })).unwrap()
                        )
                    ),
                `Channel updated for ${selectedRows.length} ${selectedRows.length === 1 ? "row" : "rows"}.`,
                "Could not update channel."
            ),
        [dispatch, runBulkAction, selectedRows]
    );

    const applyBulkNotes = useCallback(
        (notes) =>
            runBulkAction(
                () =>
                    Promise.all(
                        selectedRows.map((row) =>
                            dispatch(
                                updateInventorySkuMapping({
                                    id: row.id,
                                    body: { notes: String(notes || "").trim() || null },
                                })
                            ).unwrap()
                        )
                    ),
                `Notes updated for ${selectedRows.length} ${selectedRows.length === 1 ? "row" : "rows"}.`,
                "Could not update notes."
            ),
        [dispatch, runBulkAction, selectedRows]
    );

    const applyBulkDelete = useCallback(
        () =>
            runBulkAction(
                () =>
                    Promise.all(
                        selectedRows.map((row) => dispatch(deleteInventorySkuMapping(row.id)).unwrap())
                    ),
                `Deleted ${selectedRows.length} ${selectedRows.length === 1 ? "row" : "rows"}.`,
                "Could not delete selected rows."
            ),
        [dispatch, runBulkAction, selectedRows]
    );

    const applyBulkSupply = useCallback(
        (product) => {
            const nextSupplyProductId = product?.id === CLEAR_SUPPLY_OPTION.id ? null : product?.id;
            if (nextSupplyProductId === undefined) return;
            return runBulkAction(
                () =>
                    Promise.all(
                        selectedRows.map((row) =>
                            dispatch(
                                updateInventorySkuMapping({
                                    id: row.id,
                                    body: { supply_product_id: nextSupplyProductId },
                                })
                            ).unwrap()
                        )
                    ),
                nextSupplyProductId === null
                    ? `Supply SKU cleared for ${selectedRows.length} ${selectedRows.length === 1 ? "row" : "rows"}.`
                    : `Supply SKU updated for ${selectedRows.length} ${selectedRows.length === 1 ? "row" : "rows"}.`,
                "Could not update supply SKU."
            );
        },
        [dispatch, runBulkAction, selectedRows]
    );

    const clearBulkCurrentSupply = useCallback(async () => {
        if (selectedRows.length === 0) return;
        setBulkBusy(true);
        try {
            await Promise.all(
                selectedRows.map((row) =>
                    dispatch(
                        updateInventorySkuMapping({
                            id: row.id,
                            body: { supply_product_id: null },
                        })
                    ).unwrap()
                )
            );
            await refreshMappings();
            toast.success(
                `Supply SKU cleared for ${selectedRows.length} ${selectedRows.length === 1 ? "row" : "rows"}.`
            , { position: "bottom-right" });
        } catch (err) {
            const msg = typeof err === "string" ? err : err?.message || "Could not update supply SKU.";
            toast.error(msg, { position: "bottom-right" });
        } finally {
            setBulkBusy(false);
        }
    }, [dispatch, refreshMappings, selectedRows]);

    const acceptBulkSuggestions = useCallback(
        () =>
            runBulkAction(
                () =>
                    Promise.all(
                        selectedSuggestionRows.map((row) =>
                            dispatch(
                                updateInventorySkuMapping({
                                    id: row.id,
                                    body: { supply_product_id: row.suggested_supply_product_id },
                                })
                            ).unwrap()
                        )
                    ),
                `Accepted ${selectedSuggestionRows.length} ${selectedSuggestionRows.length === 1 ? "suggestion" : "suggestions"}.`,
                "Could not accept suggestions."
            ),
        [dispatch, runBulkAction, selectedSuggestionRows]
    );

    useEffect(() => {
        if (!openBulkAction) return;

        const onDoc = (e) => {
            if (bulkToolbarRef.current && !bulkToolbarRef.current.contains(e.target)) {
                setOpenBulkAction(null);
            }
        };
        const onKey = (e) => {
            if (e.key === "Escape") setOpenBulkAction(null);
        };

        document.addEventListener("mousedown", onDoc);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onDoc);
            document.removeEventListener("keydown", onKey);
        };
    }, [openBulkAction]);

    useEffect(() => {
        if (selectedCount === 0) {
            setOpenBulkAction(null);
        }
    }, [selectedCount]);

    const handleExportCsv = useCallback(() => {
        const isManualTab = activeTabId === "manual";
        const isAmazonTab = activeTabId === "amazon";
        const rowsToExport = isManualTab ? exportableManualRows : isAmazonTab ? filteredAmazonRows : [];

        if (!isManualTab && !isAmazonTab) {
            toast.info("CSV export is available for Manual and Amazon SKU Mapping only.", {
                position: "bottom-right",
            });
            return;
        }

        if (rowsToExport.length === 0) {
            toast.error("No rows to export for the current filters.", { position: "bottom-right" });
            return;
        }

        setExportingCsv(true);
        try {
            const headers = isManualTab
                ? [
                      "Mapping Status",
                      "Execution SKU",
                      "Product",
                      "Channel",
                      "Supply SKU",
                      "Barcode",
                      "Notes",
                      "Suggested Supply SKU",
                  ]
                : ["Execution SKU", "Product", "Type", "Supply SKU", "Barcode", "Status", "Forecastable"];
            const csvLines = [
                headers.map(escapeCsvField).join(","),
                ...rowsToExport.map((row) =>
                    (isManualTab
                        ? [
                              row.has_supply ? "Mapped" : "Unmapped",
                              row.execution_sku || "",
                              row.canonical_product_name || "",
                              row.channel || "",
                              row.supply_sku || "",
                              row.barcode || "",
                              row.notes || "",
                              row.suggested_supply_sku || "",
                          ]
                        : [
                              row.execution_sku || "",
                              row.canonical_product_name || "",
                              row.type || "",
                              row.supply_sku || "",
                              row.barcode || "",
                              row.status || "",
                              row.forecastable ? "Yes" : "No",
                          ])
                        .map(escapeCsvField)
                        .join(",")
                )
            ];

            const blob = new Blob([`\uFEFF${csvLines.join("\r\n")}`], {
                type: "text/csv;charset=utf-8;",
            });
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `sku-mapping-${activeTabId}-${new Date().toISOString().slice(0, 10)}.csv`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);

            toast.success(`Exported ${rowsToExport.length.toLocaleString()} row(s).`, {
                position: "bottom-right",
            });
        } catch (err) {
            const msg = err?.message || "CSV export failed.";
            toast.error(msg, { position: "bottom-right" });
        } finally {
            setExportingCsv(false);
        }
    }, [activeTabId, exportableManualRows, filteredAmazonRows]);

    return (
        <>
            <main className="w-full h-full p-8">
                <div className="h-full flex flex-col gap-2">
                    {/* Header */}
                    <section className="flex items-center justify-between gap-3">
                        <div className="flex flex-row gap-3">
                            <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-primary">
                                <ChartPieIcon className="w-5 h-5 text-white" />
                            </div>
                            <div className="flex flex-col gap-0">
                                <h3 className="text-xs font-normal text-gray-500">Inventory / SKU Mapping</h3>
                                <h2 className="text-base font-semibold">SKU Mapping</h2>
                            </div>
                        </div>
                        <div className="flex flex-row gap-2">
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-200 text-gray-600">
                                <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                                <span className="inline-flex items-center gap-1.5">
                                    {mappingStats === null ? (
                                        <span
                                            className="inline-flex shrink-0 items-center justify-center"
                                            aria-hidden
                                        >
                                            <LoaderSpinner
                                                width="8"
                                                height="8"
                                                border="2"
                                                colorSpinner="gray"
                                                parentHeight="h-[12px]"
                                            />
                                        </span>
                                    ) : (
                                        mappedCount.toLocaleString()
                                    )}
                                    <span>mapped</span>
                                </span>
                            </span>
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                                <span className="w-1.5 h-1.5 rounded-full bg-red-600" />
                                <span className="inline-flex items-center gap-1.5">
                                    {mappingStats === null ? (
                                        <span
                                            className="inline-flex shrink-0 items-center justify-center"
                                            aria-hidden
                                        >
                                            <LoaderSpinner
                                                width="8"
                                                height="8"
                                                border="2"
                                                colorSpinner="red"
                                                parentHeight="h-[12px]"
                                            />
                                        </span>
                                    ) : (
                                        unmappedCount.toLocaleString()
                                    )}
                                    <span>unmapped</span>
                                </span>
                            </span>
                            {(mappingsLoading && mappingItems.length === 0) || suggestionsCount > 0 ? (
                                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                                    <span className="inline-flex items-center gap-1.5">
                                        {mappingsLoading && mappingItems.length === 0 ? (
                                            <span className="inline-flex shrink-0 items-center justify-center" aria-hidden>
                                                <LoaderSpinner
                                                    width="8"
                                                    height="8"
                                                    border="2"
                                                    colorSpinner="gray"
                                                    parentHeight="h-[12px]"
                                                />
                                            </span>
                                        ) : (
                                            suggestionsCount.toLocaleString()
                                        )}
                                        <span>suggestions</span>
                                    </span>
                                </span>
                            ) : null}
                        </div>
                    </section>
                    {/* Tabs */}
                    <section className="border-b border-t border-gray-200">
                        <nav
                            className="flex flex-wrap gap-x-0 gap-y-2 -mb-px"
                            role="tablist"
                            aria-label="SKU mapping views"
                        >
                            {SKU_MAPPING_TABS.map((tab) => {
                                const isActive = activeTabId === tab.id;
                                return (
                                    <button
                                        key={tab.id}
                                        type="button"
                                        role="tab"
                                        aria-selected={isActive}
                                        id={`sku-mapping-tab-${tab.id}`}
                                        onClick={() => setActiveTabId(tab.id)}
                                        className={[
                                            "group flex items-center font-bold gap-2 px-4 py-3 text-sm border-b-2 -mb-px transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2 rounded-t",
                                            isActive
                                                ? "border-gray-900 text-gray-900"
                                                : "border-transparent text-gray-500 hover:text-gray-700",
                                        ].join(" ")}
                                    >
                                        <span>{tab.label}</span>
                                        <span
                                            className={[
                                                "inline-flex min-h-[1.375rem] min-w-[1.375rem] items-center justify-center rounded-full px-2 text-xs tabular-nums",
                                                isActive
                                                    ? "bg-gray-900 text-white"
                                                    : "bg-gray-200 text-gray-600",
                                            ].join(" ")}
                                        >
                                            {tab.id === "manual" ? (
                                                manualTotalRowCount === null ? (
                                                    <span
                                                        className="inline-flex shrink-0 items-center justify-center"
                                                        aria-hidden
                                                    >
                                                        <LoaderSpinner
                                                            width="8"
                                                            height="8"
                                                            border="2"
                                                            colorSpinner="gray"
                                                            parentHeight="h-[12px]"
                                                        />
                                                    </span>
                                                ) : (
                                                    manualTotalRowCount.toLocaleString()
                                                )
                                            ) : tab.id === "amazon" ? (
                                                amazonMappingsLoading && amazonTotalRowCount === 0 ? (
                                                    <span
                                                        className="inline-flex shrink-0 items-center justify-center"
                                                        aria-hidden
                                                    >
                                                        <LoaderSpinner
                                                            width="8"
                                                            height="8"
                                                            border="2"
                                                            colorSpinner="gray"
                                                            parentHeight="h-[12px]"
                                                        />
                                                    </span>
                                                ) : (
                                                    amazonTotalRowCount.toLocaleString()
                                                )
                                            ) : tab.id === "final" ? (
                                                finalTotalRowCount.toLocaleString()
                                            ) : (
                                                tab.count
                                            )}
                                        </span>
                                    </button>
                                );
                            })}
                        </nav>
                    </section>
                    <section>
                        {/* Filters */}
                        <section className="py-3 pt-1 border-b border-border">
                            <div className="flex flex-wrap items-center justify-between gap-3">
                                <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
                                    <div className="relative w-full min-w-[12rem] max-w-md shrink-0">
                                        <MagnifyingGlassIcon
                                            className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                                            aria-hidden
                                        />
                                        <input
                                            type="search"
                                            value={searchSku}
                                            onChange={(e) => setSearchSku(e.target.value)}
                                            placeholder="Search SKU or product..."
                                            className="px-3 py-2 w-full h-[32px] transition-all duration-200 rounded-md no-search-input-chrome border border-gray-300 bg-white pl-8 text-xs focus:outline-none focus:ring-2 focus:ring-primary pr-3"
                                            aria-label="Search SKU or product"
                                        />
                                    </div>
                                    {activeTabId !== "shopify" && activeTabId !== "amazon" && (
                                        <div className="w-full min-w-[11rem] max-w-[130px] shrink-0">
                                            <ProductCatalogSelectFilter
                                                allLabel="All Channels"
                                                options={CHANNEL_FILTER_OPTIONS}
                                                IconOfSelect={<FunnelIcon className="h-5 w-5 text-gray-900"/>}
                                                selectedOptions={selectedChannels}
                                                buttonSelectedClassname="!text-xs !h-[32px]"
                                                onChange={setSelectedChannels}
                                                renderOption={(option) => (
                                                    <span className="text-gray-900">{option.label}</span>
                                                )}
                                            />
                                        </div>
                                    )}
                                    {activeTabId === "manual" && (
                                        <button
                                            type="button"
                                            aria-pressed={unmappedOnly}
                                            onClick={() => setUnmappedOnly((v) => !v)}
                                            className={[
                                                "inline-flex px-3 py-2 h-[32px] shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-md border px-3 text-xs font-medium transition-all outline-none",
                                                "focus-visible:ring-[3px] focus-visible:ring-primary/40 disabled:pointer-events-none disabled:opacity-50",
                                                unmappedOnly
                                                    ? "border-primary text-white bg-primary text-primary-foreground hover:bg-primary/90 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
                                                    : "border border-gray-300 bg-transparent shadow-sm hover:bg-gray-100 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
                                            ].join(" ")}
                                        >
                                            <span>Unmapped Only</span>
                                            <span
                                                className={[
                                                    "inline-flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-bold tabular-nums",
                                                    unmappedOnly
                                                        ? "bg-gray-600 text-white"
                                                        : "bg-red-600 text-white",
                                                ].join(" ")}
                                            >
                                                {mappingStats === null ? (
                                                    <span
                                                        className="inline-flex shrink-0 items-center justify-center"
                                                        aria-hidden
                                                    >
                                                        <LoaderSpinner
                                                            width="8"
                                                            height="8"
                                                            border="2"
                                                            colorSpinner="gray"
                                                            parentHeight="h-[12px]"
                                                        />
                                                    </span>
                                                ) : (
                                                    unmappedCount.toLocaleString()
                                                )}
                                            </span>
                                        </button>
                                    )}
                                </div>
                                <button
                                    type="button"
                                    onClick={handleExportCsv}
                                    disabled={exportingCsv}
                                    className="inline-flex shrink-0 h-[32px] items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-900 transition-colors hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2"
                                >
                                    <ArrowDownTrayIcon className="h-4 w-4 text-gray-700" aria-hidden />
                                    {exportingCsv ? "Exporting..." : "Export CSV"}
                                </button>
                                {activeTabId === "manual" && (
                                    <>
                                        <button 
                                            className="flex flex-row items-center gap-2 px-3 py-1.5 h-[32px] bg-primary rounded-md text-sm text-white transition-all duration-200 hover:bg-gray-700"
                                            onClick={() => {
                                                setAddRowOpened(true);
                                            }}
                                        >
                                            <PlusIcon className="h-5 w-5 text-white" />
                                            <p className="text-sm font-medium text-white">Add Row</p>
                                        </button>
                                        <button 
                                            className="flex flex-row items-center gap-2 px-3 py-1.5 h-[32px] bg-primary rounded-md text-sm text-white transition-all duration-200 hover:bg-gray-700"
                                            onClick={() => {
                                                setAddProductOpened(true);
                                            }}
                                        >
                                            <PlusIcon className="h-5 w-5 text-white" />
                                            <p className="text-sm font-medium text-white">Add Product</p>
                                        </button>
                                    </>
                                )}
                            </div>
                        </section>
                        {activeTabId === "manual" && selectedCount > 0 ? (
                            <section
                                ref={bulkToolbarRef}
                                className="relative flex flex-wrap items-center justify-between gap-3 border-x border-b border-black bg-[#0b0f17] px-4 py-2 text-white"
                            >
                                <div className="flex flex-wrap items-center gap-2">
                                    <span className="inline-flex h-8 items-center rounded-md bg-[#111827] px-3 text-sm font-semibold text-white">
                                        {selectedCount} selected
                                    </span>

                                    <div className="relative">
                                        <BulkActionButton
                                            Icon={ArrowsRightLeftIcon}
                                            label="Change Supply SKU"
                                            onClick={() =>
                                                setOpenBulkAction((prev) => (prev === "supply" ? null : "supply"))
                                            }
                                            isOpen={openBulkAction === "supply"}
                                            disabled={bulkBusy}
                                        />
                                        {openBulkAction === "supply" ? (
                                            <BulkSupplyEditor
                                                rows={selectedRows}
                                                busy={bulkBusy}
                                                loadCatalogSkus={loadCatalogSkus}
                                                onApply={applyBulkSupply}
                                                onClearCurrent={clearBulkCurrentSupply}
                                            />
                                        ) : null}
                                    </div>

                                    <div className="relative">
                                        <BulkActionButton
                                            Icon={TagIcon}
                                            label="Change Channel"
                                            onClick={() =>
                                                setOpenBulkAction((prev) => (prev === "channel" ? null : "channel"))
                                            }
                                            isOpen={openBulkAction === "channel"}
                                            disabled={bulkBusy}
                                        />
                                        {openBulkAction === "channel" ? (
                                            <BulkChannelEditor
                                                rows={selectedRows}
                                                busy={bulkBusy}
                                                onApply={applyBulkChannel}
                                            />
                                        ) : null}
                                    </div>

                                    <div className="relative">
                                        <BulkActionButton
                                            Icon={DocumentTextIcon}
                                            label="Edit Notes"
                                            onClick={() =>
                                                setOpenBulkAction((prev) => (prev === "notes" ? null : "notes"))
                                            }
                                            isOpen={openBulkAction === "notes"}
                                            disabled={bulkBusy}
                                        />
                                        {openBulkAction === "notes" ? (
                                            <BulkNotesEditor
                                                rows={selectedRows}
                                                busy={bulkBusy}
                                                onApply={applyBulkNotes}
                                            />
                                        ) : null}
                                    </div>

                                        <div className="relative">
                                            <BulkActionButton
                                                Icon={SparklesIcon}
                                                label={`Accept Suggestions (${selectedSuggestionsCount})`}
                                                onClick={acceptBulkSuggestions}
                                                disabled={bulkBusy || selectedSuggestionsCount === 0}
                                            />
                                        </div>

                                    <div className="relative">
                                        <BulkActionButton
                                            Icon={TrashIcon}
                                            label="Delete"
                                            onClick={() =>
                                                setOpenBulkAction((prev) => (prev === "delete" ? null : "delete"))
                                            }
                                            isOpen={openBulkAction === "delete"}
                                            disabled={bulkBusy}
                                            danger
                                        />
                                        {openBulkAction === "delete" ? (
                                            <BulkDeleteConfirm
                                                rows={selectedRows}
                                                busy={bulkBusy}
                                                onCancel={() => setOpenBulkAction(null)}
                                                onDelete={applyBulkDelete}
                                            />
                                        ) : null}
                                    </div>
                                </div>

                                <button
                                    type="button"
                                    className="inline-flex h-8 items-center gap-1.5 rounded-md px-2 text-xs font-medium text-white/70 transition-colors hover:bg-white/5 hover:text-white"
                                    onClick={resetBulkSelection}
                                >
                                    <XMarkIcon className="h-4 w-4" aria-hidden />
                                    Clear
                                </button>
                            </section>
                        ) : null}
                        {/* Content */}
                        <section className="flex items-center justify-between px-4 py-2 border border-border border-t-0 bg-gray-50">
                            <span className="text-xs font-normal text-gray-500">{getPreviewOfTheTable(activeTabId)}</span>
                            {activeTabId === "manual" ? (
                                <span className="text-xs font-normal text-gray-500">
                                    {manualRowCount} rows shown
                                </span>
                            ) : activeTabId === "amazon" ? (
                                <span className="text-xs font-normal text-gray-500">
                                    {filteredAmazonRows.length} rows shown
                                </span>
                            ) : activeTabId === "final" ? (
                                <span className="text-xs font-normal text-gray-500">
                                    {filteredFinalRows.length} rows shown
                                </span>
                            ) : null}
                        </section>
                        <section className="min-h-0 flex-1">
                            {activeTabId === "manual" ? (
                                <SkuMappingManualTable
                                    searchSku={searchSku}
                                    selectedChannels={selectedChannels}
                                    unmappedOnly={unmappedOnly}
                                    refreshKey={manualTableRefreshKey}
                                    selectionResetKey={selectionResetKey}
                                    onFilteredCountChange={setManualRowCount}
                                    onMappingChanged={loadMappingStats}
                                    onSelectionChange={setSelectedMappingIds}
                                />
                            ) : activeTabId === "amazon" ? (
                                <AmazonMappingTable rows={filteredAmazonRows} loading={amazonMappingsLoading} />
                            ) : activeTabId === "final" ? (
                                <FinalMappingTable
                                    rows={filteredFinalRows}
                                    totalRows={finalTotalRowCount}
                                    loading={mappingsLoading || amazonMappingsLoading}
                                />
                            ) : (
                                <div className="px-4 py-10 text-center text-sm text-gray-500">
                                    This view is not available for the selected tab yet.
                                </div>
                            )}
                        </section>
                    </section>
                </div>
            </main>
            <AddRowModal
                isOpen={addRowOpened}
                onClose={() => setAddRowOpened(false)}
                onSuccess={() => setManualTableRefreshKey((k) => k + 1)}
            />
            <AddProductDrawer
                isOpen={addProductOpened}
                onClose={() => setAddProductOpened(false)}
            />
        </>
    )
};

export default SKUMapping;